// Turns a 402 `no_active_plan` from central-api into the plan lock.
//
// The app reaches the API through `asyncHandler` and through some thirty
// per-feature `fetch` helpers. Rather than teaching each of them about the
// refusal, one watcher on `window.fetch` reads every 402 and reports it here;
// `asyncHandler` reports its own as well so it can stay quiet about it.

import store from '../../store/store';
import { lockForNoActivePlan } from '../../store/reducers/authSlice';
import { isNoActivePlanResponse } from './plan-lock-rules';

/**
 * The credentials in memory right now. Taken before a request and handed back
 * with its answer, it tells a refusal for this sign-in from a late one that
 * belongs to an account that has since signed out on this tab.
 */
export function currentSignIn(): string {
  const { authorization, agency } = store.getState().auth;
  return `${agency?.accessToken ?? ''}|${
    authorization.accessToken?.sellerToken ?? ''
  }`;
}

/**
 * Lock the app after a 402 `no_active_plan`. Safe to call for every refused
 * request: only the first one changes the state. A refusal that arrives after
 * sign-out (no credential left in memory) is ignored, so it cannot lock the
 * next account that signs in on this tab.
 */
export function reportNoActivePlan(body: unknown, sentAs?: string): void {
  const { authorization, agency } = store.getState().auth;
  if (authorization.subscriptionLock === 'true') {
    return;
  }
  // The request went out under another sign-in than the current one: its
  // refusal says nothing about this account.
  if (sentAs !== undefined && sentAs !== currentSignIn()) {
    return;
  }
  const signedIn =
    !!agency?.accessToken ||
    !!authorization.accessToken?.sellerToken ||
    !!agency?.employeeIdentity;
  if (!signedIn) {
    return;
  }
  const { plan_status, message } = (body ?? {}) as {
    plan_status?: unknown;
    message?: unknown;
  };
  store.dispatch(lockForNoActivePlan({ plan_status, message }));
}

/**
 * Report `response` when it is the plan refusal. Reads a clone, so the caller
 * can still consume the body. Returns whether it was the refusal.
 */
export async function reportIfNoActivePlan(
  response: Response,
  sentAs?: string
): Promise<boolean> {
  if (response?.status !== 402) {
    return false;
  }
  try {
    const body = await response.clone().json();
    if (isNoActivePlanResponse(response.status, body)) {
      reportNoActivePlan(body, sentAs);
      return true;
    }
  } catch {
    // Not JSON: some other 402, handled by the caller as before.
  }
  return false;
}

let installed = false;

/** Watch every `fetch` answer for the plan refusal. Installed once at startup. */
export function installPlanLockWatcher(): void {
  if (installed || typeof window === 'undefined' || !window.fetch) return;
  installed = true;

  const realFetch = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const sentAs = currentSignIn();
    const response = await realFetch(input as RequestInfo, init);
    if (response?.status === 402) {
      // Never let the watcher break or delay the request it observes.
      reportIfNoActivePlan(response, sentAs).catch(() => undefined);
    }
    return response;
  };
}
